import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import CustomerToolbar from './CustomerToolbar';

const TableCustomers = props => {
  const {reload, setReload, selected, setSelected } = props;
  const [loading, setLoading] = useState(true)  
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('username')  
  const currencyLocale = Intl.NumberFormat('ko-KR');

  const loadCustomer = 
    async (query, sort, column) => {
      await apiService.get('/members/corp', {
        params: {
          query,
          sort,
          column,
          page,
          pageSize
        }
      }).then(response => {
        const payload = response.data;
        setTotal(payload.size)
        setList(payload.list)
        
        setLoading(false)
      }).catch(e => console.error(e));
    }
    
  useEffect(() => {
    loadCustomer(searchValue, sort, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadCustomer(searchValue, newModel[0].sort, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('username')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadCustomer(value, sort, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      minWidth: 50,
      field: 'username',
      headerName: '사용자 ID',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.username}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'com_name',
      headerName: '업체명',
      renderCell: params => (
        <Link to={`/admin/company/${params.row.com_id}`} >
          {params.row.com_name}
        </Link>
      )
    },
    {
      minWidth: 300,
      headerName: '주소',
      field: 'address',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.com_zip + ' ' + params.row.com_addr1 + ' ' + params.row.com_addr2}
        </Typography>
      )
    },    
    {
      minWidth: 150,
      field: 'phone',
      headerName: '전화번호',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.phone}
        </Typography>
      )
    },
    {
      minWidth: 150,
      field: 'last_visit',
      headerName: '최근방문일자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.last_visit? moment(params.row.last_visit).format('YYYY-MM-DD'): ''}
        </Typography>
      )
    },
    {
      minWidth: 140,
      field: 'com_purchase',
      headerName: '매출액',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {currencyLocale.format(params.row.com_purchase)}원
        </Typography>
      )
    },
    {
      minWidth: 300,
      field: 'com_memo',
      headerName: '메모',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.com_memo}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'com_charge',
      headerName: '담당자',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.com_charge}
        </Typography>
      )
    },
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='고객 내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.username}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: CustomerToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.username),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableCustomers
